import { yupResolver } from '@hookform/resolvers/yup';
import { t } from 'i18next';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Card,
  CardContent,
  Divider,
  InputAdornment,
  MenuItem,
  TextField,
  Typography
} from '@mui/material';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';

import { useBoolean } from 'src/hooks/use-boolean';
import { useCustomFields } from 'src/hooks/use-custom-fields';

import { LinkType } from 'src/services/common/common.types';
import { useGetCompanyByIdQuery, useUpdateLocalCompanyMutation } from "src/services/connections/connections.service";
import { CompanyProfileLink, CompanyProfileType } from 'src/services/connections/connections.types';
import { CustomFieldEntityType } from 'src/services/custom-fields/custom-field.types';
import { CompanySizeRange } from 'src/services/organisation/organisation.types';
import { useGetCountriesQuery } from 'src/services/utils/utils.service';
import { useAppSelector } from 'src/store/store';

import { Box } from '@mui/system';
import FormProvider, { RHFSelect, RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import { LinkedContactsCard } from 'src/sections/contacts/components/linked-contacts-card';
import { LinkTypeMeta } from 'src/services/common.types';
import { ContactLinkEntityType } from 'src/services/contacts/contacts.types';
import AccountOwnerCard from './account-owner-card';
import { CompanyLabelsCard } from './company-labels-card';
import CompanyLinksCard from './company-links-card';
import PrimaryContactCard from './primary-contact-card';

type Props = {
  companyProfileId: string;
};

export const LinkTypeConfig = [
  {
    type: LinkType.GITHUB,
    label: LinkTypeMeta[LinkType.GITHUB].label,
    icon: LinkTypeMeta[LinkType.GITHUB].icon,
  },
  {
    type: LinkType.LINKEDIN,
    label: LinkTypeMeta[LinkType.LINKEDIN].label,
    icon: LinkTypeMeta[LinkType.LINKEDIN].icon,
  },
  {
    type: LinkType.X,
    label: LinkTypeMeta[LinkType.X].label,
    icon: LinkTypeMeta[LinkType.X].icon,
  },
  {
    type: LinkType.FACEBOOK,
    label: LinkTypeMeta[LinkType.FACEBOOK].label,
    icon: LinkTypeMeta[LinkType.FACEBOOK].icon,
  }
]

export const CompanyProfileSection = ({ companyProfileId }: Props) => {

  const showEditUserDialog = useBoolean();

  const customFields = useCustomFields({
    entityType: CustomFieldEntityType.COMPANY,
    entityId: companyProfileId
  });

  // change to getCompanyByIdQuery
  const { data: companyProfile } = useGetCompanyByIdQuery({ profile_id: companyProfileId }, {
    skip: !companyProfileId,
  });

  const { data: countries } = useGetCountriesQuery();

  const users = useAppSelector((state) => state?.data?.users);

  const [updateCompany, { isLoading: isUpdating }] = useUpdateLocalCompanyMutation();

  const [photoURL, setPhotoURL] = useState<string>('');

  const UpdateCompanyProfileSchema = Yup.object().shape({
    company_name: Yup.string().required(t('validation.required')),
    company_information: Yup.string()
      .required(t('auth.validation.company_information.required'))
      .max(2000, t('auth.validation.company_information.max'))
      .min(10, t('auth.validation.company_information.min')),
    company_logo_id: Yup.string().nullable(),
    company_size: Yup.mixed<CompanySizeRange>()
      .transform((value) => (value === '' || value === undefined ? null : value))
      .oneOf(Object.values(CompanySizeRange), t('auth.validation.company_size.type_error')).nullable(),
    company_website: Yup.string().url(t('auth.validation.company_website.valid')).nullable(),
    company_phone: Yup.string().nullable(),
    country_code: Yup.string().nullable(),

    primary_address_street: Yup.string().nullable(),
    primary_address_city: Yup.string().nullable(),
    primary_address_state: Yup.string().nullable(),

    links: Yup.array().of(
      Yup.object().shape({
        type: Yup.string().required(),
        url: Yup.string()
          .url(t('validation.url'))
          .nullable(),
      })
    ),
  });

  const defaultValues = {
    company_name: '',
    company_information: '',
    company_logo_id: '',
    company_website: '',
    company_size: '' as CompanySizeRange,
    country_code: '',
    company_phone: '',
    links: LinkTypeConfig.map(meta => ({
      type: meta.type,
      url: '',
    })),
    primary_address_street: '',
    primary_address_city: '',
    primary_address_state: ''
  };

  const methods = useForm({
    resolver: yupResolver(UpdateCompanyProfileSchema),
    defaultValues,
  });

  const {
    setValue,
    handleSubmit,
    reset,
    trigger,
    control,
    watch,
    formState: { isSubmitting, errors },
  } = methods;

  const links = watch('links') as CompanyProfileLink[];

  useEffect(() => {
    if (!companyProfile) return;

    setPhotoURL(companyProfile.company_logo?.public_path ?? '');

    const initialLinks = LinkTypeConfig.map(typeMeta => {
      const existingLink = companyProfile.links?.find(link => link.type === typeMeta.type);

      return {
        type: typeMeta.type as LinkType,
        url: existingLink?.url || '',
      };
    });

    reset({
      company_name: companyProfile.company_name ?? '',
      company_information: companyProfile.company_information ?? '',
      company_logo_id: companyProfile.company_logo?.id ?? '',
      company_website: companyProfile.company_website ?? '',
      company_size: companyProfile.company_size ?? '' as CompanySizeRange,
      country_code: companyProfile.country_code ?? '',
      company_phone: companyProfile.company_phone ?? '',
      links: initialLinks,
      primary_address_street: companyProfile.primary_address?.street ?? '',
      primary_address_city: companyProfile.primary_address?.city ?? '',
      primary_address_state: companyProfile.primary_address?.state ?? ''
    });
  }, [companyProfile, reset]);


  const onSubmit = handleSubmit(async (data) => {

    if (!companyProfile) return;

    // Validate custom fields first
    const customFieldValidation = customFields.validate();
    if (!customFieldValidation.isValid) {
      return;
    }

    try {

      const submitData = {
        company_name: data.company_name,
        company_information: data.company_information,
        company_logo_id: data.company_logo_id as string,
        company_website: data.company_website as string,
        company_size: data.company_size as CompanySizeRange,
        country_code: data.country_code ?? undefined,
        company_phone: data.company_phone as string,
        links: data.links?.filter((link) => link.url).map((link) => ({
          type: link.type as LinkType,
          url: link.url as string,
        })),
        primary_address: {
          street: data.primary_address_street ?? '',
          city: data.primary_address_city ?? '',
          state: data.primary_address_state ?? '',
          country_code: data.country_code ?? ''
        },
        custom_fields: customFields.getData()
      }

      await updateCompany({
        profile_id: companyProfileId,
        data: submitData
      }).unwrap();

      enqueueSnackbar(t('companies.profile.api.update.success'), { variant: 'success' });
    } catch (error) {

      enqueueSnackbar(t('companies.profile.api.update.default_error'), { variant: 'error' });
    }
  });

  const companySizeOptions = useMemo(
    () =>
      Object.values(CompanySizeRange).map((size) => ({
        value: size,
        label: t(`enums.company_size_ranges.${size}`),
      })),
    []
  );

  const onEditPhoto = (url: string) => {
    setPhotoURL(url);
    setValue('company_logo_id', url);
    onSubmit();
  };

  const onLinkChange = useCallback((index: number, value: string) => {
    const updatedLinks = [...links];
    updatedLinks[index] = { ...updatedLinks[index], url: value };

    setValue('links', updatedLinks, { shouldValidate: true, shouldDirty: true });

  }, [links, setValue]);

  const detailsEditable = useCallback((field: string) => {
    if (!companyProfile) return false;

    if (companyProfile.type === CompanyProfileType.UNLINKED) {
      return true;
    }

    if (companyProfile.type === CompanyProfileType.LINKED) {
      switch (field) {
        case 'company_name':
          return false;
        default:
          return true;
      }
    }

    return false;
  }, [companyProfile]);

  return (
    <Card>
      <CardContent>
        <FormProvider methods={methods} onSubmit={onSubmit}>
          <Grid container spacing={1}>
            <Grid xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                {t('admin-organization.profile.headers.company_info')}
              </Typography>
            </Grid>

            {
              !detailsEditable &&
              <Grid xs={12}>
                <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                  {t('admin-organization.profile.alerts.linked_profile_locked')}
                </Alert>
              </Grid>
            }

            <Grid xs={12}>
              <RHFTextField
                fullWidth
                disabled={!detailsEditable('company_name')}
                name='company_name'
                label={t('common.form_labels.company_name')}
                placeholder={t('common.placeholders.company_name')}
                error={methods.formState.errors.company_name !== undefined}
                helperText={methods.formState.errors.company_name?.message}
              />
            </Grid>

            <Grid xs={12}>
              <Stack display="unset">
                <RHFTextField
                  multiline
                  disabled={!detailsEditable('company_information')}
                  name="company_information"
                  label={t('common.form_labels.company_information')}
                  rows={3}
                />
              </Stack>
            </Grid>

            <Grid xs={12}>
              <RHFSelect
                disabled={!detailsEditable('company_size')}
                inputProps={{ 'data-testid': 'company_size' }}
                name="company_size"
                label={t('auth.form-labels.company_size')}
              >
                {!companySizeOptions && (
                  <MenuItem key="default" value={undefined}>
                    {' '}
                  </MenuItem>
                )}
                {companySizeOptions?.map((size, index) => (
                  <MenuItem key={`company-size-${size.value}-${index}`} value={size.value}>
                    {size.label}
                  </MenuItem>
                ))}
              </RHFSelect>
            </Grid>

            <Grid xs={12}>
              <RHFTextField
                disabled={!detailsEditable('company_phone')}
                name="company_phone"
                label={t('common.form_labels.company_phone')}
                placeholder={t('common.placeholders.company_phone')}
              />
            </Grid>

            <Grid xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                {t('admin-organization.profile.headers.address')}
              </Typography>
            </Grid>

            <Grid xs={12}>
              <RHFTextField
                disabled={!detailsEditable('primary_address_street')}
                name="primary_address_street"
                label={t('common.form_labels.street')}
                placeholder={t('common.placeholders.street')}
              />
            </Grid>

            <Grid xs={4}>
              <RHFTextField
                disabled={!detailsEditable('primary_address_street')}
                name="primary_address_city"
                label={t('common.form_labels.city')}
                placeholder={t('common.placeholders.city')}
              />
            </Grid>

            <Grid xs={4}>
              <RHFTextField
                disabled={!detailsEditable('primary_address_street')}
                name="primary_address_state"
                label={t('common.form_labels.state')}
                placeholder={t('common.placeholders.state')}
              />
            </Grid>

            <Grid xs={4}>
              <RHFSelect
                disabled={!detailsEditable('country_code')}
                onChange={(event) => {
                  setValue('country_code', event.target.value);
                }}
                inputProps={{ 'data-testid': 'country_code' }}
                name="country_code"
                label={t('common.form_labels.country')}
              >
                {!countries && (
                  <MenuItem key="default" value={undefined}>
                    {' '}
                  </MenuItem>
                )}
                {countries?.map((country, index) => (
                  <MenuItem key={`country-${country.code}-${index}`} value={country.code}>
                    {country.name}
                  </MenuItem>
                ))}
              </RHFSelect>
            </Grid>

            {
              customFields.hasCustomFields && (
                <>
                  <Grid xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('admin-organization.profile.headers.custom_fields')}
                    </Typography>
                  </Grid>

                  {customFields.renderCustomFields}
                </>
              )
            }

            <Grid xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                {t('admin-organization.profile.headers.links')}
              </Typography>
            </Grid>

            <Grid xs={12}>
              <Stack display="unset">
                <RHFTextField
                  disabled={!detailsEditable('company_website')}
                  type='url'
                  name="company_website"
                  label={t('common.form_labels.company_website')}
                  placeholder={t('common.placeholders.company_website')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Iconify icon="iconoir:www" sx={{ color: 'text.disabled' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
            </Grid>

            {LinkTypeConfig.map((typeMeta, index) => (
              <Grid xs={12} key={index}>
                <Controller
                  name={`links.${index}.url`}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      disabled={!detailsEditable('links')}
                      label={typeMeta.label}
                      placeholder={`Enter your ${typeMeta.type} url`}
                      type="url"
                      onChange={(event) => onLinkChange(index, event.target.value)}
                      error={!!error}
                      helperText={error?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Iconify icon={typeMeta.icon} sx={{ color: 'text.disabled' }} />
                          </InputAdornment>
                        ),
                      }}
                    />

                  )}
                />
              </Grid>
            ))}

          </Grid>

          <Stack spacing={3} alignItems="flex-end" sx={{ mt: 3 }}>
            <LoadingButton type="submit" variant="contained" loading={isSubmitting} disabled={isUpdating || !detailsEditable}>
              {t('common.save')}
            </LoadingButton>
          </Stack>

        </FormProvider>
      </CardContent>
    </Card>
  )
};